import { createStore, type StoreApi } from 'zustand';
import {
  isObsoleto,
  nuovoIdComando,
  SOGLIA_OBSOLESCENZA_CLOUD_MS,
  type Allarme,
  type AzioneComando,
  type Energia,
  type EsitoComando,
  type EventoDispositivo,
  type EventoStato,
  type Impianto,
  type Lettura,
  type MotivoFallimento,
  type RispostaComando,
  type StatoAttuatore,
  type StatoImpianto,
  type StatoSorgente,
} from '@emacomfort/domain';
import type { DeviceClient } from '@emacomfort/device-client';

/** Stato locale di un comando: tre stati distinti, più il fallimento (RF-14). */
export interface StatoComando {
  idComando: string;
  azione: AzioneComando;
  /** Chiave di aggregazione per la UI: `zona:<id>`, `modalita`, `sanitario`, `vmc`, `globale`, `profilo`, `programma:<id>`. */
  oggetto: string;
  esito: EsitoComando;
  motivo?: MotivoFallimento;
  ts: number;
}

export interface StatoDispositivo {
  impianto: Impianto | null;
  letture: Record<string, Lettura>;
  attuatori: Record<string, StatoAttuatore>;
  stato: StatoImpianto | null;
  energia: Record<string, Energia>;
  sorgenti: Record<string, StatoSorgente>;
  allarmi: Allarme[];
  eventi: EventoStato[];
  ultimaLettura: number | null;
  adesso: number;
  comandi: Record<string, StatoComando>;
  pronto: boolean;
}

export type DeviceStore = StoreApi<StatoDispositivo>;

export function oggettoDi(azione: AzioneComando): string {
  switch (azione.azione) {
    case 'set_setpoint':
    case 'set_zona_abilitata':
      return `zona:${azione.zonaId}`;
    case 'set_setpoint_globale':
      return 'globale';
    case 'set_modalita':
      return 'modalita';
    case 'set_sanitario':
      return 'sanitario';
    case 'set_vmc':
      return 'vmc';
    case 'applica_profilo':
      return 'profilo';
    case 'set_programma':
      return `programma:${azione.zonaId}`;
  }
}

export function creaDeviceStore(client: DeviceClient): { store: DeviceStore; avvia(): () => void; invia(azione: AzioneComando): Promise<RispostaComando> } {
  const store = createStore<StatoDispositivo>(() => ({
    impianto: null,
    letture: {},
    attuatori: {},
    stato: null,
    energia: {},
    sorgenti: {},
    allarmi: [],
    eventi: [],
    ultimaLettura: null,
    adesso: Date.now(),
    comandi: {},
    pronto: false,
  }));

  const riduci = (evento: EventoDispositivo): void => {
    const s = store.getState();
    switch (evento.tipo) {
      case 'letture':
        store.setState({ letture: { ...s.letture, ...evento.valori }, ultimaLettura: evento.ts });
        break;
      case 'attuatore':
        store.setState({ attuatori: { ...s.attuatori, [evento.stato.canale]: evento.stato } });
        break;
      case 'stato':
        store.setState({ stato: evento.stato, impianto: s.impianto ? { ...s.impianto, zone: evento.stato.zone, modalita: evento.stato.modalita } : s.impianto });
        break;
      case 'energia':
        store.setState({ energia: { ...s.energia, [evento.sorgenteId]: evento.energia } });
        break;
      case 'sorgente':
        store.setState({ sorgenti: { ...s.sorgenti, [evento.sorgente.id]: evento.sorgente } });
        break;
      case 'allarme': {
        const altri = s.allarmi.filter((a) => a.id !== evento.allarme.id);
        store.setState({ allarmi: [evento.allarme, ...altri].sort((a, b) => b.ts - a.ts) });
        break;
      }
      case 'evento':
        store.setState({ eventi: [evento.evento, ...s.eventi].slice(0, 200) });
        break;
      case 'esito_comando': {
        const c = s.comandi[evento.idComando];
        if (!c) return;
        store.setState({ comandi: { ...s.comandi, [evento.idComando]: { ...c, esito: evento.esito, motivo: evento.motivo, ts: evento.ts } } });
        break;
      }
    }
  };

  const avvia = (): (() => void) => {
    const annulla = client.sottoscrivi(riduci);
    void client.getImpianto().then((impianto) => {
      const ist = client.getIstantanea();
      store.setState({
        impianto,
        letture: ist.letture,
        attuatori: ist.attuatori,
        stato: ist.stato,
        energia: ist.energia,
        sorgenti: ist.sorgenti,
        allarmi: ist.allarmi.sort((a, b) => b.ts - a.ts),
        ultimaLettura: ist.ts || null,
        pronto: true,
      });
    });
    void client.getEventi().then((eventi) => store.setState({ eventi }));
    const orologio = setInterval(() => store.setState({ adesso: Date.now() }), 1000);
    return () => {
      annulla();
      clearInterval(orologio);
    };
  };

  const invia = async (azione: AzioneComando): Promise<RispostaComando> => {
    const idComando = nuovoIdComando();
    const s = store.getState();
    store.setState({ comandi: { ...s.comandi, [idComando]: { idComando, azione, oggetto: oggettoDi(azione), esito: 'in_invio', ts: Date.now() } } });
    const risposta = await client.inviaComando({ ...azione, idComando });
    // La voce resta visibile qualche secondo, poi sparisce.
    setTimeout(() => {
      const { comandi } = store.getState();
      if (!comandi[idComando]) return;
      const rest = { ...comandi };
      delete rest[idComando];
      store.setState({ comandi: rest });
    }, risposta.esito === 'fallito' ? 6000 : 2500);
    return risposta;
  };

  return { store, avvia, invia };
}

/**
 * Se i dati mostrati non sono attuali.
 *
 * La soglia dipende da COME l'impianto parla, non da una costante unica. Una
 * centralina nostra pubblica a ogni variazione: un minuto di silenzio è un
 * guasto. Una sorgente di terze parti si interroga ogni pochi minuti: quel
 * minuto è il suo funzionamento normale, e misurarla con il metro della
 * centralina la dichiarava morta mentre rispondeva.
 *
 * Per una sorgente di terze parti conta anche il suo verdetto: è LEI a sapere
 * quando il produttore ha smesso di rispondere, e lo dice in
 * `StatoSorgente.raggiungibile`. La soglia resta comunque applicata, così un
 * ciclo di poll che si ferma del tutto (scheda sospesa, timer perso) degrada
 * dopo quindici minuti invece di mostrare per sempre l'ultimo verdetto buono.
 */
export function selezionaObsoleto(s: StatoDispositivo): boolean {
  const terzeParti = s.impianto?.centralina.tipo === 'terze_parti';
  if (!terzeParti) return isObsoleto(s.ultimaLettura, s.adesso);

  const sorgenti = Object.values(s.sorgenti);
  const nessunaRaggiungibile = sorgenti.length > 0 && !sorgenti.some((x) => x.raggiungibile);
  return nessunaRaggiungibile || isObsoleto(s.ultimaLettura, s.adesso, SOGLIA_OBSOLESCENZA_CLOUD_MS);
}

/** L'ultimo comando per un oggetto, se ancora in vista. */
export function selezionaComando(oggetto: string) {
  return (s: StatoDispositivo): StatoComando | undefined => {
    let ultimo: StatoComando | undefined;
    Object.values(s.comandi).forEach((c) => {
      if (c.oggetto === oggetto && (!ultimo || c.ts > ultimo.ts)) ultimo = c;
    });
    return ultimo;
  };
}
